import { WORLDS } from '../models/world';
import { type LevelProgress, type ProgressMap } from '../services/progress';

export type AchievementCategory = 'progression' | 'skill' | 'mastery' | 'discovery';

export interface AchievementDef {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly category: AchievementCategory;
  readonly iconName: string;
}

const FIRST_STEPS: AchievementDef = {
  id: 'first_steps',
  title: 'First Steps',
  description: 'Complete your first level',
  category: 'progression',
  iconName: 'explore',
};

const CARTOGRAPHER: AchievementDef = {
  id: 'cartographer',
  title: 'Cartographer',
  description: 'Complete all levels in any world',
  category: 'progression',
  iconName: 'map',
};

const GRAND_ENGINEER: AchievementDef = {
  id: 'grand_engineer',
  title: 'Grand Engineer',
  description: '3-star every level in any world',
  category: 'progression',
  iconName: 'engineering',
};

const MASTER_OF_TRADES: AchievementDef = {
  id: 'master_of_trades',
  title: 'Master of All Trades',
  description: 'Complete at least 1 level in every world',
  category: 'progression',
  iconName: 'public',
};

const PERFECTIONIST: AchievementDef = {
  id: 'perfectionist',
  title: 'Perfectionist',
  description: '3-star all 60 campaign levels',
  category: 'progression',
  iconName: 'workspace_premium',
};

const NO_HINTS: AchievementDef = {
  id: 'no_hints',
  title: 'No Hints Needed',
  description: 'Complete a level without using any hints',
  category: 'skill',
  iconName: 'lightbulb',
};

const SPEED_RUNNER: AchievementDef = {
  id: 'speed_runner',
  title: 'Speed Runner',
  description: 'Complete a level in under 30 seconds',
  category: 'skill',
  iconName: 'timer',
};

const FIRST_TRY: AchievementDef = {
  id: 'first_try',
  title: 'First Try',
  description: 'Get 3 stars on first attempt',
  category: 'skill',
  iconName: 'stars',
};

const STREAK_5: AchievementDef = {
  id: 'streak_5',
  title: 'On a Roll',
  description: 'Complete 5 levels in a row without resetting',
  category: 'skill',
  iconName: 'local_fire_department',
};

const MASTER_SHORTEST_PATH: AchievementDef = {
  id: 'master_shortest_path',
  title: 'Pathfinder',
  description: '3-star all shortest path levels',
  category: 'mastery',
  iconName: 'route',
};

const MASTER_MST: AchievementDef = {
  id: 'master_mst',
  title: 'Network Architect',
  description: '3-star all MST levels',
  category: 'mastery',
  iconName: 'hub',
};

const MASTER_MAX_FLOW: AchievementDef = {
  id: 'master_max_flow',
  title: 'Flow Master',
  description: '3-star all max flow levels',
  category: 'mastery',
  iconName: 'water_drop',
};

const MASTER_MIN_CUT: AchievementDef = {
  id: 'master_min_cut',
  title: 'Surgeon',
  description: '3-star all min cut levels',
  category: 'mastery',
  iconName: 'content_cut',
};

const MASTER_MATCHING: AchievementDef = {
  id: 'master_matching',
  title: 'Matchmaker',
  description: '3-star all matching levels',
  category: 'mastery',
  iconName: 'handshake',
};

const MASTER_INTERDICTION: AchievementDef = {
  id: 'master_interdiction',
  title: 'Saboteur',
  description: '3-star all interdiction levels',
  category: 'mastery',
  iconName: 'dangerous',
};

const WORST_PATH: AchievementDef = {
  id: 'worst_path',
  title: 'Terrible Navigator',
  description: 'Submit a solution that earns 0 stars',
  category: 'discovery',
  iconName: 'wrong_location',
};

const UNDECISIVE: AchievementDef = {
  id: 'undecisive',
  title: 'Undecisive',
  description: 'Undo 10+ times in a single level',
  category: 'discovery',
  iconName: 'undo',
};

const EXPLORER: AchievementDef = {
  id: 'explorer',
  title: 'Explorer',
  description: 'Select every edge in a graph before submitting',
  category: 'discovery',
  iconName: 'travel_explore',
};

const REVISIONIST: AchievementDef = {
  id: 'revisionist',
  title: 'Revisionist',
  description: 'Replay and complete a level you already 3-starred',
  category: 'discovery',
  iconName: 'replay',
};

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  FIRST_STEPS,
  CARTOGRAPHER,
  GRAND_ENGINEER,
  MASTER_OF_TRADES,
  PERFECTIONIST,
  NO_HINTS,
  SPEED_RUNNER,
  FIRST_TRY,
  STREAK_5,
  MASTER_SHORTEST_PATH,
  MASTER_MST,
  MASTER_MAX_FLOW,
  MASTER_MIN_CUT,
  MASTER_MATCHING,
  MASTER_INTERDICTION,
  WORST_PATH,
  UNDECISIVE,
  EXPLORER,
  REVISIONIST,
];

const BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a] as const));

export function achievementById(id: string): AchievementDef | undefined {
  return BY_ID.get(id);
}

export function achievementsByCategory(category: string): AchievementDef[] {
  return ACHIEVEMENTS.filter((a) => a.category === category);
}

export const ACHIEVEMENT_CATEGORIES: readonly AchievementCategory[] = [
  'progression',
  'skill',
  'mastery',
  'discovery',
];

const CATEGORY_LABELS: Readonly<Record<AchievementCategory, string>> = {
  progression: 'Progression',
  skill: 'Skill',
  mastery: 'Mastery',
  discovery: 'Discovery',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category as AchievementCategory] ?? category;
}

// ─── Checker ────────────────────────────────────────────────────────────────

const MASTERY_WORLD_IDS: Readonly<Record<string, readonly number[]>> = {
  master_shortest_path: [1, 2],
  master_mst: [3],
  master_max_flow: [4],
  master_min_cut: [5],
  master_matching: [6],
  master_interdiction: [7],
};

export interface AchievementCheckInput {
  /** Progress that already includes THIS level's result. */
  readonly progress: ProgressMap;
  readonly alreadyEarnedIds: ReadonlySet<string>;
  readonly levelId: string;
  readonly starsEarned: number;
  readonly hintsUsed: number;
  readonly timeTakenSeconds: number;
  readonly undoCount: number;
  readonly totalEdgesInGraph: number;
  readonly edgesSelectedDuringSession: number;
  readonly wasAlreadyCompleted: boolean;
  readonly wasAlready3Starred: boolean;
}

function totalCompletedLevels(progress: ProgressMap): number {
  return Object.keys(progress).length;
}

function completedLevelsInWorld(progress: ProgressMap, worldId: number): number {
  const prefix = `w${worldId}_`;
  return Object.keys(progress).filter((id) => id.startsWith(prefix)).length;
}

function worldAll3Stars(progress: ProgressMap, worldId: number, totalLevels: number): boolean {
  const prefix = `w${worldId}_`;
  const count3Star = Object.entries(progress).filter(
    ([id, lp]: [string, LevelProgress]) => id.startsWith(prefix) && lp.stars >= 3,
  ).length;
  return count3Star >= totalLevels;
}

/**
 * Evaluates all achievement conditions after a level is completed and
 * returns the IDs of newly earned achievements (not already earned).
 */
export function checkAfterLevelComplete(input: AchievementCheckInput): string[] {
  const {
    progress,
    alreadyEarnedIds,
    starsEarned,
    hintsUsed,
    timeTakenSeconds,
    undoCount,
    totalEdgesInGraph,
    edgesSelectedDuringSession,
    wasAlreadyCompleted,
    wasAlready3Starred,
  } = input;

  const earned: string[] = [];
  const grant = (id: string): void => {
    if (!alreadyEarnedIds.has(id) && !earned.includes(id)) earned.push(id);
  };

  if (totalCompletedLevels(progress) >= 1) grant('first_steps');

  if (WORLDS.some((world) => completedLevelsInWorld(progress, world.id) >= world.totalLevels)) {
    grant('cartographer');
  }

  if (WORLDS.some((world) => worldAll3Stars(progress, world.id, world.totalLevels))) {
    grant('grand_engineer');
  }

  if (WORLDS.every((world) => completedLevelsInWorld(progress, world.id) >= 1)) {
    grant('master_of_trades');
  }

  if (WORLDS.every((world) => worldAll3Stars(progress, world.id, world.totalLevels))) {
    grant('perfectionist');
  }

  if (hintsUsed === 0) grant('no_hints');
  if (timeTakenSeconds < 30) grant('speed_runner');
  if (starsEarned === 3 && !wasAlreadyCompleted) grant('first_try');

  for (const [achievementId, worldIds] of Object.entries(MASTERY_WORLD_IDS)) {
    if (worldIds.every((worldId) => worldAll3Stars(progress, worldId, worldTotalLevels(worldId)))) {
      grant(achievementId);
    }
  }

  if (starsEarned === 0) grant('worst_path');
  if (undoCount >= 10) grant('undecisive');
  if (totalEdgesInGraph > 0 && edgesSelectedDuringSession >= totalEdgesInGraph) grant('explorer');
  if (wasAlready3Starred && starsEarned > 0) grant('revisionist');

  return earned;
}

function worldTotalLevels(worldId: number): number {
  return WORLDS.find((w) => w.id === worldId)?.totalLevels ?? 0;
}

/** Returns 'streak_5' when the streak newly crosses the threshold. */
export function checkStreak(
  alreadyEarnedIds: ReadonlySet<string>,
  streakCount: number,
): string | null {
  if (streakCount >= 5 && !alreadyEarnedIds.has('streak_5')) return 'streak_5';
  return null;
}
